import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import WidgetApiService from '@/common/services/widget-api.service';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
import { TransactTypes } from '@/types/transact';
import { v4 as uuid } from 'uuid';
import { capitalizeFirstLetter, formatString, networkCodes, networkCoins, networkTypes, } from '@/utils/coins';
import { getChannelLabel, getChannelType, isValidWalletAddress, } from '@/utils/helpers';
import CountryConfigDefaults from '@/utils/constants';
import router, { RoutePaths } from '@/router';
import { CountryTypes } from '@/types/country';
import { KycTypes } from '@/types/kyc';
import { PaymentTypes } from '@/types/payment';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
import { useAuthStore } from './auth';
import { usePaymentStore } from './payment';
import { useKycStore } from './kyc';
import { useGeneralStore } from './general';
import { useInterceptorStore } from './interceptor';
var TransactionType = TransactTypes.TransactionType;
var SupportedCountriesConfig = CountryTypes.SupportedCountriesConfig;
const useTransactionsStore = defineStore('transactions', () => {
    const selectedCrypto = ref({
        token: TransactTypes.Tokens.USDT,
        editable: true,
        standard: TransactTypes.Network.TRC20,
        networkType: networkTypes.TRON,
    });
    const selectedCryptoAmount = ref({
        amount: 0,
        editable: true,
    });
    const currencyCoinSelect = ref('Currency');
    const selectedCurrency = ref({
        currency: '',
        editable: true,
    });
    const selectedCurrencyAmount = ref({
        amount: 0,
        editable: true,
    });
    const selectedFiatAmountWithoutFees = ref(0);
    const usdRate = ref(0);
    const currentRate = ref(0);
    const maxTransactionAmount = ref(10000);
    const minTransactionAmount = ref(500);
    const loading = ref(false);
    const loadingMessage = ref('');
    const loadingErrorMessage = ref('');
    const walletAddress = ref({ address: '', editable: true, tag: '' });
    const refundAddress = ref(undefined);
    const refundToSourceAddress = ref(undefined);
    const walletTagRequired = ref(false);
    const selectedChannel = ref();
    const selectedChannelId = ref('');
    const buyFlow = ref(true);
    const serviceFee = ref(0);
    const serviceFeeUSD = ref(0);
    const networkFee = ref(0);
    const signature = ref('');
    const loadingError = ref(false);
    const pollForBankDetails = ref(false);
    const txType = ref(TransactTypes.TransactionType.BUY);
    const sequenceId = ref(undefined);
    const error = ref(undefined);
    const minipayError = ref('');
    const showSideMenu = ref(false);
    const selectedTransaction = ref(null);
    const userTransactions = ref([]);
    const fetchingUserTransactions = ref(false);
    const transferFound = ref(false);
    const cryptoTransferSent = ref(false);
    const depositFailed = ref(false);
    const collection = ref(null);
    const payment = ref(null);
    const paymentFound = ref(true);
    const rates = ref([]);
    const channels = ref([]);
    const channel = ref();
    const countryConfig = ref({});
    const countryConfigs = ref([]);
    const currentStep = ref(TransactTypes.TransactSteps.BUY_1);
    const markStep = ref(TransactTypes.TransactSteps.BUY_1);
    const buyCryptoList = ref([]);
    const limitError = ref(false);
    const dailyLimitError = ref(false);
    const monthlyLimitError = ref(false);
    const lifetimeLimitError = ref(false);
    const fullCountryConfigsArray = ref([]);
    const defaultCountryConfig = ref();
    const disableTransactionToggle = ref(false);
    const authStore = useAuthStore();
    const { token, partnerSessionId, deviceId, partnerKey, travelRuleConfig, partnerId, } = storeToRefs(authStore);
    const paymentStore = usePaymentStore();
    const { sellPaymentAccountName, sellPaymentAccountNumber, sellPaymentBranchCode, selectedPaymentNetwork, userMomoAccountNumber, currentStep: paymentCurrentStep, } = storeToRefs(paymentStore);
    const kycStore = useKycStore();
    const { requireDoubleIdNG, kycStatus } = storeToRefs(kycStore);
    const activeSupportedCountryConfig = ref();
    const generalStore = useGeneralStore();
    const { widgetFlowsEnabled } = storeToRefs(generalStore);
    const interceptorStore = useInterceptorStore();
    const { error: interceptorError } = storeToRefs(interceptorStore);
    const { ChannelTypes } = WidgetApiServiceTypes;
    const shouldTriggerTravelRule = computed(() => {
        if (travelRuleConfig.value?.status !== 'ACTIVE')
            return false;
        const buyLocal = selectedCurrencyAmount.value.amount;
        const buyUSD = buyLocal / usdRate.value;
        if (buyUSD < travelRuleConfig.value.minAmount)
            return false;
        return true;
    });
    function resetStore() {
        selectedCurrencyAmount.value.amount = 0;
        selectedCryptoAmount.value.amount = 0;
        selectedFiatAmountWithoutFees.value = 0;
        walletAddress.value = { address: '', editable: true, tag: '' };
        refundAddress.value = undefined;
        refundToSourceAddress.value = undefined;
        serviceFee.value = 0;
        serviceFeeUSD.value = 0;
        networkFee.value = 0;
        buyCryptoList.value.splice(0);
        disableTransactionToggle.value = false;
    }
    function resetUserAndPartnerStore() {
        resetStore();
        rates.value.splice(0);
        currentStep.value = TransactTypes.TransactSteps.BUY_1;
        buyCryptoList.value.splice(0);
    }
    function calculateCryptoAmount(amount) {
        const usdAmount = amount / usdRate.value;
        return usdAmount / currentRate.value;
    }
    function calculateFiatAmount(cryptoAmount) {
        const usdAmount = usdRate.value * currentRate.value;
        return cryptoAmount * usdAmount;
    }
    function resetFees() {
        serviceFee.value = 0;
        networkFee.value = 0;
        selectedCurrencyAmount.value.amount = selectedFiatAmountWithoutFees.value;
        selectedCryptoAmount.value.amount = calculateCryptoAmount(selectedCurrencyAmount.value.amount);
    }
    function getAvailableCrypto() {
        const list = rates.value.reduce((acc, value) => {
            if (value.locale !== 'crypto' || !value.buy) {
                return acc;
            }
            const item = {
                name: formatString(value.rateId),
                rateId: value.rateId,
                symbol: value.code,
            };
            const networks = networkCoins[item.symbol];
            if (!networks) {
                acc.push(item);
                return acc;
            }
            networks.forEach(coin => {
                let name = formatString(value.rateId);
                if (networks.length > 1) {
                    name = `${name} on ${capitalizeFirstLetter(networkCodes[coin.networkType], true)}`;
                }
                acc.push({
                    ...item,
                    ...coin,
                    name,
                });
            });
            return acc;
        }, []);
        buyCryptoList.value.splice(0);
        list.forEach(item => {
            buyCryptoList.value.push(item);
        });
    }
    function setCountryConfig(param) {
        if (param.currency && param.countryCode) {
            countryConfig.value = countryConfigs.value.find(config => config.currency === param.currency &&
                config.country === param.countryCode);
        }
        else if (param.currency) {
            countryConfig.value = countryConfigs.value.find(config => config.currency === param.currency);
        }
        else if (param.countryCode) {
            countryConfig.value = countryConfigs.value.find(config => config.country === param.countryCode);
        }
        requireDoubleIdNG.value =
            countryConfig.value.country === CountryTypes.CountryCodes.NIGERIA;
        activeSupportedCountryConfig.value =
            SupportedCountriesConfig[countryConfig.value.country];
        if (txType.value === TransactionType.BUY) {
            minTransactionAmount.value =
                countryConfig.value?.buyMin || CountryConfigDefaults.min;
            maxTransactionAmount.value =
                countryConfig.value?.buyMax || CountryConfigDefaults.max;
        }
        else {
            minTransactionAmount.value =
                countryConfig.value?.sellMin || CountryConfigDefaults.min;
            maxTransactionAmount.value =
                countryConfig.value?.sellMax || CountryConfigDefaults.max;
        }
    }
    function setSelectedCurrency(params) {
        const { currency, countryCode, editable = true } = params;
        selectedCurrency.value = { currency, editable };
        setCountryConfig({ currency, countryCode });
    }
    function processQueryCurrency(query) {
        if (!query.localCurrency)
            return;
        const partnerRates = rates.value.filter(rate => rate.locale !== 'crypto');
        let transactionRate = partnerRates.find(rate => rate.code === query.localCurrency?.toUpperCase());
        if (!transactionRate) {
            const errorMessage = `[Query Params]: partner rate does not exist for currency ${query.localCurrency}`;
            console.error(errorMessage);
            throw new Error(errorMessage);
        }
        if (!['XOF', 'XAF'].includes(query.localCurrency.toUpperCase())) {
            setSelectedCurrency({
                currency: query.localCurrency.toUpperCase(),
                countryCode: transactionRate.locale.toUpperCase(),
                editable: false,
            });
            return;
        }
        if (!query.country) {
            setSelectedCurrency({
                currency: query.localCurrency.toUpperCase(),
                countryCode: transactionRate.locale.toUpperCase(),
                editable: true,
            });
            return;
        }
        const supportedCountry = SupportedCountriesConfig[query.country.toUpperCase()];
        if (!['XAF', 'XOF'].includes(supportedCountry?.currency)) {
            const errorMessage = `[Query Params]: provided country is not valid for provided currency - <currency: ${query.localCurrency}; country: ${query.country}>`;
            console.error(errorMessage);
            throw new Error(errorMessage);
        }
        transactionRate = partnerRates.find(rate => rate.code === query.localCurrency?.toUpperCase() &&
            rate.locale === query.country?.toUpperCase());
        if (!transactionRate) {
            const errorMessage = `[Query Params]: partner rate does not exist for currency ${query.localCurrency} and country ${query.country}`;
            console.error(errorMessage);
            throw new Error(errorMessage);
        }
        setSelectedCurrency({
            currency: query.localCurrency.toUpperCase(),
            countryCode: query.country.toUpperCase(),
            editable: false,
        });
    }
    function setFromQueryParams(query) {
        processQueryCurrency(query);
        if (query.currencyAmount) {
            selectedCurrencyAmount.value = {
                amount: query.currencyAmount,
                editable: false,
            };
        }
        let _token = query.token?.toLocaleUpperCase();
        if (!Object.keys(networkCoins).includes(_token)) {
            _token = TransactTypes.Tokens.USDT;
        }
        const selected = networkCoins[_token];
        const defaultSelect = selected[0];
        let networkType = query.network?.toLocaleUpperCase() ?? defaultSelect?.networkType;
        if (!networkCodes[networkType])
            networkType = defaultSelect?.networkType;
        const { standard: _standard, networkType: _networkType } = selected.find((token_) => token_.networkType === networkType) ||
            defaultSelect;
        selectedCrypto.value = {
            token: _token,
            editable: !query.token,
            networkType: _networkType,
            standard: _standard,
        };
        // if the wallet address is passed but no token or no signature, we throw an error
        loadingError.value =
            !!query.walletAddress &&
                (!query.token || !query.signature) &&
                query.txType !== 'sell';
        if (loadingError.value) {
            throw new Error('invalid query params passed - no token');
        }
        if (!query.txType || query.txType === TransactionType.BUY) {
            walletAddress.value = {
                address: query.walletAddress ?? '',
                editable: !query.walletAddress,
                tag: query.tag ?? '',
            };
        }
        if (query.txType === TransactionType.SELL &&
            query.refundAddress &&
            !query.signature) {
            loadingError.value = true;
            throw new Error('invalid query params passed - refundAddress requires signature');
        }
        if (query.txType === TransactionType.SELL) {
            refundAddress.value = query.refundAddress;
            refundToSourceAddress.value =
                query.refundToSourceAddress !== undefined
                    ? String(query.refundToSourceAddress) === 'true'
                    : undefined;
        }
        if (query.cryptoAmount) {
            selectedCryptoAmount.value = {
                amount: Number(query.cryptoAmount),
                editable: false,
            };
        }
        selectedChannelId.value = query.channelId ?? '';
        if (query.channelType) {
            selectedChannel.value = query.channelType;
        }
        signature.value = query.signature ?? '';
        if ((query.did && (!query.txType || query.txType !== TransactionType.KYC)) ||
            (query.txType === TransactionType.KYC && !query.did)) {
            throw new Error('txType and did mismatch');
        }
        disableTransactionToggle.value =
            !!query.txType ||
                !(widgetFlowsEnabled.value.includes('buy') &&
                    widgetFlowsEnabled.value.includes('sell'));
        const queryTxType = query.txType?.toLowerCase();
        if (queryTxType) {
            if (queryTxType !== 'kyc' &&
                !widgetFlowsEnabled.value.includes(queryTxType)) {
                console.error(`${queryTxType} flow not enabled`);
                throw new Error(`${queryTxType} transactions not enabled`);
            }
            txType.value = queryTxType;
        }
        else {
            switch (true) {
                case widgetFlowsEnabled.value.includes('buy'):
                    txType.value = TransactionType.BUY;
                    break;
                case widgetFlowsEnabled.value.includes('sell'):
                    txType.value = TransactionType.SELL;
                    break;
                default:
                    console.error(`no widget flows enabled`);
                    throw new Error('widget flows not enabled');
            }
        }
        sequenceId.value = query.sequenceId ?? undefined;
    }
    function setSelectedCrypto(crypto, standard, networkType) {
        selectedCrypto.value = {
            ...selectedCrypto.value,
            token: crypto,
            standard,
            networkType,
        };
    }
    function getCurrencyRate(currency) {
        const rate = rates.value.find(item => item.code === currency);
        return rate?.buy;
    }
    function getCurrencySellRate(currency) {
        const rate = rates.value.find(item => item.code === currency);
        return rate?.sell;
    }
    function getNetwork(isPayment = false) {
        const { token: token_ } = selectedCrypto.value;
        const tokens = TransactTypes.TokenNetworkMapping[token_] || [];
        let { standard, networkType: networkType_ } = selectedCrypto.value;
        standard = standard?.toLocaleUpperCase();
        if (!standard) {
            return tokens[0] || '';
        }
        networkType_ = networkType_?.toLocaleUpperCase();
        const allowedNetworks = [
            networkTypes.CELO,
            networkTypes.BASE,
            networkTypes.POLYGON,
        ].map(nt => nt.toLocaleUpperCase());
        if (networkType_ && allowedNetworks.includes(networkType_)) {
            return networkType_;
        }
        if (networkCoins[token_] &&
            networkType_ &&
            !isPayment) {
            return standard;
        }
        return tokens.find(_token => _token.includes(standard)) || standard;
    }
    const network = computed(() => {
        const { standard: standard_, token: token_, networkType: networkType_, } = selectedCrypto.value;
        if (networkType_)
            return networkType_.toLocaleUpperCase();
        const tokens = TransactTypes.TokenNetworkMapping[token_] || [];
        if (standard_) {
            return (tokens.find(_token => _token.includes(standard_.toLocaleUpperCase())) ||
                standard_);
        }
        return tokens[0];
    });
    async function getRates() {
        try {
            const response = await WidgetApiService.GetRates(partnerSessionId.value, deviceId.value);
            rates.value = response;
            getAvailableCrypto();
            return response;
        }
        catch (e) {
            throw new Error(`${e.message}`);
        }
    }
    function setFees() {
        serviceFee.value = channel.value?.serviceFee?.feeLocal ?? 0;
        serviceFeeUSD.value = channel.value?.serviceFee?.feeUSD ?? 0;
        selectedFiatAmountWithoutFees.value = selectedCurrencyAmount.value.amount;
        switch (txType.value) {
            case TransactTypes.TransactionType.BUY:
                selectedCurrencyAmount.value.amount =
                    selectedCurrencyAmount.value.amount -
                        serviceFee.value -
                        networkFee.value;
                selectedCryptoAmount.value.amount = calculateCryptoAmount(selectedCurrencyAmount.value.amount);
                break;
            case TransactTypes.TransactionType.SELL:
                selectedCurrencyAmount.value.amount -= serviceFee.value;
                break;
            default:
        }
    }
    function modifyAndSortTransactions(data) {
        let { payments, collections } = data;
        payments = payments.map((payment_) => ({
            ...payment_,
            transactionType: 'payment',
        }));
        collections = collections.map((collection_) => ({
            ...collection_,
            transactionType: 'collection',
        }));
        return [...payments, ...collections].sort((a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf());
    }
    async function getUserTransactions() {
        try {
            if (!pollForBankDetails.value) {
                loading.value = true;
                loadingMessage.value = 'Fetching your transactions';
            }
            fetchingUserTransactions.value = true;
            const response = await WidgetApiService.GetUserTransactions(token.value, partnerSessionId.value, deviceId.value);
            userTransactions.value = modifyAndSortTransactions(response);
            fetchingUserTransactions.value = false;
            return response;
        }
        catch (e) {
            throw new Error(`${e.message}`);
        }
        finally {
            loading.value = false;
            loadingMessage.value = '';
        }
    }
    function setCurrencyRates() {
        if (txType.value === TransactTypes.TransactionType.BUY) {
            usdRate.value = getCurrencyRate(selectedCurrency.value.currency);
            currentRate.value = getCurrencyRate(selectedCrypto.value.token);
            selectedCryptoAmount.value.amount = calculateCryptoAmount(selectedCurrencyAmount.value.amount);
        }
        if (txType.value === TransactTypes.TransactionType.SELL) {
            usdRate.value = getCurrencySellRate(selectedCurrency.value.currency);
            currentRate.value = getCurrencySellRate(selectedCrypto.value.token);
            selectedCurrencyAmount.value.amount = calculateFiatAmount(selectedCryptoAmount.value.amount);
        }
    }
    function setTagRequired() {
        walletTagRequired.value = [
            TransactTypes.Network.TON,
            TransactTypes.Network.XLM,
            TransactTypes.Network.XRP,
        ].includes(selectedCrypto.value.networkType);
    }
    function setCryptoRates() {
        if (txType.value === TransactTypes.TransactionType.BUY) {
            usdRate.value = getCurrencyRate(selectedCurrency.value.currency);
            currentRate.value = getCurrencyRate(selectedCrypto.value.token);
            selectedCryptoAmount.value.amount = calculateCryptoAmount(selectedCurrencyAmount.value.amount);
        }
        if (txType.value === TransactTypes.TransactionType.SELL) {
            usdRate.value = getCurrencySellRate(selectedCurrency.value.currency);
            currentRate.value = getCurrencySellRate(selectedCrypto.value.token);
            selectedCurrencyAmount.value.amount = calculateFiatAmount(selectedCryptoAmount.value.amount);
        }
    }
    function getMinMax() {
        const countryFiatMax = countryConfig.value.buyMax;
        if (!usdRate.value || !currentRate.value) {
            setCurrencyRates();
            setCryptoRates();
        }
        const cryptoMax = countryFiatMax / (usdRate.value * currentRate.value);
        const countryFiatMin = countryConfig.value.buyMin;
        const cryptoMin = countryFiatMin / (usdRate.value * currentRate.value);
        return { min: cryptoMin, max: cryptoMax };
    }
    async function getWithdrawChannelsWithoutFees(channelType) {
        if (!activeSupportedCountryConfig.value) {
            throw new Error('cannot source channels without country config');
        }
        const response = await WidgetApiService.GetChannels({
            rampType: 'withdraw',
            currency: selectedCurrency.value.currency,
            sessionId: partnerSessionId.value,
            deviceId: deviceId.value,
            withoutFees: true,
        });
        return response.channels.filter(item => item.country === activeSupportedCountryConfig.value?.countryCode &&
            item.channelType === channelType);
    }
    async function getChannelsWithFees(rampType) {
        if (!usdRate.value || !currentRate.value) {
            setCurrencyRates();
            setCryptoRates();
        }
        try {
            let { networkType } = selectedCrypto.value;
            networkType = networkType?.toLocaleUpperCase();
            if (networkType !== TransactTypes.Network.CELO) {
                networkType =
                    selectedCrypto.value.standard?.toLocaleUpperCase() || networkType;
            }
            if (!activeSupportedCountryConfig.value) {
                throw new Error('cannot source channels without country config');
            }
            const response = await WidgetApiService.GetChannels({
                currency: selectedCurrency.value.currency,
                sessionId: partnerSessionId.value,
                deviceId: deviceId.value,
                rate: usdRate.value,
                amount: currentRate.value * selectedCryptoAmount.value.amount,
                cryptoAmount: selectedCryptoAmount.value.amount,
                cryptoCurrency: selectedCrypto.value.token,
                rampType: rampType === TransactTypes.TransactionType.BUY
                    ? 'deposit'
                    : 'withdraw',
                cryptoNetwork: networkType,
            });
            networkFee.value = (response.networkFees?.feeUSD ?? 0) * usdRate.value;
            return response.channels.filter(item => activeSupportedCountryConfig.value?.providers.includes(item.channelType) && item.country === activeSupportedCountryConfig.value.countryCode);
        }
        catch (e) {
            throw new Error(`${e.message}`);
        }
    }
    async function createOrder(travelRuleData) {
        try {
            if (!channel.value?.id) {
                throw new Error(`missing required params for collection creation`);
            }
            if (selectedChannel.value === WidgetApiServiceTypes.ChannelTypes.MOMO &&
                !selectedPaymentNetwork.value) {
                throw new Error(`Select a payment network`);
            }
            let order = {
                channelId: channel.value?.id,
                currency: selectedCurrency.value.currency,
                country: countryConfig.value.country,
                localAmount: Number(selectedFiatAmountWithoutFees.value),
                sequenceId: sequenceId.value || uuid(),
                apiKey: partnerKey.value,
                settlementInfo: {
                    walletAddress: walletAddress.value.address,
                    cryptoCurrency: selectedCrypto.value.token,
                    cryptoNetwork: getNetwork(),
                    walletTag: walletAddress.value.tag,
                },
                signature: signature.value,
                // any valid URL is fine here
                redirectUrl: `https://widget.yellowcard.io`,
                source: selectedChannel.value === WidgetApiServiceTypes.ChannelTypes.MOMO
                    ? {
                        accountNumber: `${countryConfig.value.countryCode}${userMomoAccountNumber.value}`,
                        networkId: selectedPaymentNetwork.value?.id ?? '',
                    }
                    : undefined,
            };
            if (shouldTriggerTravelRule.value) {
                order = {
                    ...order,
                    travelRuleData,
                };
            }
            collection.value = await WidgetApiService.CreateOrder(token.value, order, partnerSessionId.value, deviceId.value);
            buyFlow.value = true;
        }
        catch (e) {
            throw new Error('Failed to create order');
        }
    }
    function setPaymentFound() {
        const { Status } = WidgetApiServiceTypes;
        if (!payment.value) {
            paymentFound.value = false;
            return;
        }
        paymentFound.value = [
            Status.CREATED,
            Status.PENDING,
            Status.PENDING_APPROVAL,
            Status.COMPLETE,
            Status.PROCESS,
            Status.PROCESSING,
            Status.PENDING_CONFIRMATION,
            Status.PENDING_SETTLEMENT,
        ].includes(payment.value.status);
    }
    async function fetchPayment() {
        if (payment.value !== null) {
            try {
                payment.value = await WidgetApiService.FetchPayment(token.value, partnerSessionId.value, deviceId.value, payment.value.id);
                setPaymentFound();
            }
            catch (e) {
                throw new Error('failed to get payment');
            }
        }
    }
    async function fetchCollection() {
        if (collection.value) {
            try {
                collection.value = await WidgetApiService.FetchCollection(token.value, partnerSessionId.value, deviceId.value, collection.value.id);
            }
            catch (e) {
                throw new Error('failed to get payment');
            }
        }
    }
    async function cancelPayment() {
        if (!payment.value)
            return;
        try {
            payment.value = await WidgetApiService.CancelPayment(token.value, partnerSessionId.value, deviceId.value, { paymentId: payment.value.id });
            setPaymentFound();
        }
        catch (e) {
            throw new Error('failed to cancel payment');
        }
    }
    async function createSellOrder() {
        try {
            if (!channel.value?.id) {
                throw new Error(`missing required params for payment creation`);
            }
            const paymentSequenceId = sequenceId.value || uuid();
            sequenceId.value = paymentSequenceId;
            const order = {
                apiKey: partnerKey.value,
                channelId: channel.value?.id,
                localAmount: Number(selectedFiatAmountWithoutFees.value),
                reason: 'other',
                signature: signature.value,
                destination: {
                    accountName: sellPaymentAccountName.value,
                    accountNumber: selectedChannel.value === WidgetApiServiceTypes.ChannelTypes.MOMO
                        ? `${countryConfig.value.countryCode}${userMomoAccountNumber.value}`
                        : sellPaymentAccountNumber.value,
                    accountType: getChannelType(selectedChannel.value),
                    networkId: selectedPaymentNetwork.value.id,
                    branchCode: sellPaymentBranchCode.value,
                },
                currency: selectedCurrency.value.currency,
                country: countryConfig.value.country,
                sequenceId: paymentSequenceId,
                settlementInfo: {
                    cryptoCurrency: selectedCrypto.value.token,
                    cryptoNetwork: getNetwork(true),
                    cryptoAmount: selectedCryptoAmount.value.amount,
                    ...(refundAddress.value !== undefined && {
                        refundAddress: refundAddress.value,
                    }),
                    ...(refundToSourceAddress.value !== undefined && {
                        refundToSourceAddress: refundToSourceAddress.value,
                    }),
                },
            };
            payment.value = await WidgetApiService.CreateSellOrder(token.value, order, partnerSessionId.value, deviceId.value);
            setPaymentFound();
        }
        catch (e) {
            error.value = 'Failed to create sell order';
            throw new Error(error.value);
        }
    }
    function getTransactionStatus(status, type) {
        if (type === 'payment') {
            if (status === 'complete') {
                return 'complete';
            }
            if (status === 'pending_refund') {
                return 'failed';
            }
        }
        switch (status) {
            case 'created':
            case 'processing':
            case 'pending':
            case 'complete':
            case 'settlement_pending':
            case 'settlement_processing':
                return 'pending';
            case 'failed':
            case 'settlement_failed':
                return 'failed';
            case 'expired':
                return 'expired';
            case 'settlement_complete':
                return 'complete';
            case 'refunded':
                return 'refunded';
            default:
                return 'pending';
        }
    }
    function getMinipayOrWalletRoute() {
        const MINIPAY_TOKENS = [
            TransactTypes.Tokens.CUSD,
            TransactTypes.Tokens.USDT,
            TransactTypes.Tokens.USDC,
        ];
        if (!MINIPAY_TOKENS.includes(selectedCrypto.value.token)) {
            paymentCurrentStep.value = PaymentTypes.SellPaymentSteps.PAYMENT_2;
            return;
        }
        if (selectedCrypto.value.networkType !== networkTypes.CELO) {
            paymentCurrentStep.value = PaymentTypes.SellPaymentSteps.PAYMENT_2;
            return;
        }
        const IS_MINIPAY = window.ethereum?.isMiniPay || false;
        paymentCurrentStep.value = IS_MINIPAY
            ? PaymentTypes.SellPaymentSteps.PAYMENT_5 // authorise minipay
            : PaymentTypes.SellPaymentSteps.PAYMENT_2; // wallet details
    }
    const getWalletDetails = async () => {
        loading.value = true;
        loadingMessage.value = 'Submitting your order';
        error.value = undefined;
        try {
            await createSellOrder();
            if (kycStatus.value !== KycTypes.KycStatus.APPROVED) {
                await router.push('/kyc');
            }
            else {
                getMinipayOrWalletRoute();
                await router.push(RoutePaths.Payment);
            }
        }
        catch (e) {
            error.value = 'Failed to create sell order';
            throw new Error('Failed to create sell order');
        }
        finally {
            loading.value = false;
            loadingMessage.value = '';
        }
    };
    async function getBankDetails(travelRuleData) {
        error.value = '';
        interceptorError.value = '';
        const fullAddress = walletTagRequired.value
            ? `${walletAddress.value.address}_${walletAddress.value.tag}`
            : walletAddress.value.address;
        const addressValid = isValidWalletAddress({
            address: fullAddress,
            token: selectedCrypto.value.token,
            standard: selectedCrypto.value.standard,
            networkType: selectedCrypto.value.networkType,
        });
        if (!addressValid) {
            error.value = 'Invalid wallet address and network pair';
            paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_1;
            await router.push(RoutePaths.Payment);
            return;
        }
        if (selectedChannel.value === ChannelTypes.MOMO) {
            paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_3;
            await router.push(RoutePaths.Payment);
            return;
        }
        trackEvent(MIXPANEL_EVENTS.BUY.WALLET_ADDRESS, {
            payment_method: getChannelLabel(selectedChannel.value),
            partner: partnerId.value,
        });
        error.value = undefined;
        loading.value = true;
        loadingMessage.value = 'Submitting your order';
        try {
            loading.value = true;
            await createOrder(travelRuleData);
            if (kycStatus.value !== KycTypes.KycStatus.APPROVED) {
                paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_1;
                await router.push(RoutePaths.Kyc);
            }
            else {
                paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_2;
                await router.push(RoutePaths.Payment);
            }
        }
        catch (e) {
            error.value = 'Failed to create order';
            paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_1;
            await router.push(RoutePaths.Payment);
        }
        finally {
            loading.value = false;
        }
        loading.value = false;
        loadingMessage.value = '';
    }
    watch(selectedCurrency, () => {
        setCurrencyRates();
    });
    watch(selectedCrypto, () => {
        setCryptoRates();
        setTagRequired();
    });
    watch(limitError, () => {
        if (!limitError.value)
            return;
        router.push('/limit');
        limitError.value = !limitError.value;
    });
    return {
        setSelectedCrypto,
        setSelectedCurrency,
        setFromQueryParams,
        selectedCrypto,
        loadingError,
        selectedCurrency,
        selectedCurrencyAmount,
        selectedCryptoAmount,
        calculateCryptoAmount,
        usdRate,
        currentRate,
        maxTransactionAmount,
        minTransactionAmount,
        rates,
        getRates,
        channels,
        getWithdrawChannelsWithoutFees,
        getChannelsWithFees,
        getCurrencyRate,
        countryConfig,
        setCountryConfig,
        countryConfigs,
        activeSupportedCountryConfig,
        networkFee,
        loading,
        loadingMessage,
        walletAddress,
        refundAddress,
        refundToSourceAddress,
        signature,
        transferFound,
        cryptoTransferSent,
        depositFailed,
        channel,
        selectedChannel,
        selectedChannelId,
        currentStep,
        userTransactions,
        selectedTransaction,
        getUserTransactions,
        showSideMenu,
        createOrder,
        createSellOrder,
        getBankDetails,
        collection,
        currencyCoinSelect,
        buyFlow,
        markStep,
        serviceFee,
        resetFees,
        setFees,
        buyCryptoList,
        serviceFeeUSD,
        selectedFiatAmountWithoutFees,
        resetStore,
        loadingErrorMessage,
        pollForBankDetails,
        txType,
        limitError,
        calculateFiatAmount,
        getCurrencySellRate,
        fetchingUserTransactions,
        resetUserAndPartnerStore,
        fetchPayment,
        payment,
        paymentFound,
        getTransactionStatus,
        fetchCollection,
        cancelPayment,
        fullCountryConfigsArray,
        defaultCountryConfig,
        dailyLimitError,
        lifetimeLimitError,
        monthlyLimitError,
        getWalletDetails,
        error,
        minipayError,
        disableTransactionToggle,
        network,
        getMinMax,
        walletTagRequired,
        sequenceId,
        shouldTriggerTravelRule,
        getMinipayOrWalletRoute,
    };
});
export default useTransactionsStore;
